import { useContext, useCallback, useEffect, useRef, useState } from "react"
import classNames from "classnames"
import { Box, Button, IconButton, Typography } from "@mui/material"
import { Close, TuneOutlined } from "@mui/icons-material"
import { AnimatedLoadingButton, Setup, Rules, Error } from "./components"
import { useOptimization } from "hooks/optimizer"
import useDebouncedCallback from "hooks/debounced-callback"
import { LOADING, ERROR } from "helpers/data-statuses"
import { getDefaultConfiguration, type FormSections } from "./configuration-sections"
import { transformConfigurationToForm, transformFormToConfiguration } from "./components/helpers/configuration"
import { OptimizeExperimentsProps } from "./types"
import { useIsDashboardOpLayoutEnabled } from "resources/account"
import { useOptimizeForm, OptimizeFormContext } from "./form-context"
import { useTotalRisk } from "./hooks/use-total-risk"

import "./styles.scss"

export default function OptimizeExperiments({ context, modelMetadata }: OptimizeExperimentsProps) {
  const isDashboardOpLayoutEnabled = useIsDashboardOpLayoutEnabled()
  const {
    setIsOptimizedExperimentsDrawerOpen,
    isOptimizedExperimentsDrawerOpen,
    endDate,
    modelId,
    base,
    setComparisonInstrument,
    setOptimizeConfiguration,
    setPreviewOptimizeConfiguration,
    previewOptimizeConfiguration,
    optimizeConfiguration,
    instrument,
  } = useContext(context)
  const [totalRisk, totalRiskStatus] = useTotalRisk(instrument, modelId, endDate, base)
  const totalRiskValue = totalRisk?.values.totalRisk?.value

  const getInitialConfiguration = () => {
    const configuration = previewOptimizeConfiguration || optimizeConfiguration

    if (configuration) {
      try {
        return transformConfigurationToForm(configuration)
      } catch {
        return getDefaultConfiguration()
      }
    }

    return getDefaultConfiguration()
  }

  const [formError, setFormError] = useState<string | null>(null)
  const initialEndDate = useRef(endDate)

  const form = useOptimizeForm({
    defaultValues: getInitialConfiguration(),
    onSubmit: async ({ value }: { value: FormSections }) => {
      if (canOptimize) {
        try {
          const currentConfiguration = transformFormToConfiguration(endDate, value.setup, value.rules)
          setFormError(null)
          isSaveRef.current = true
          runOptimization()
          setOptimizeConfiguration(currentConfiguration)
          setPreviewOptimizeConfiguration(null)
        } catch {
          isSaveRef.current = false
          setFormError("Invalid form input. Please check your inputs and try again.")
        }
      }
    },
  })

  const [formValues, setFormValues] = useState(form.state.values)

  useEffect(() => {
    const unsubscribe = form.store.subscribe(() => {
      setFormValues(form.state.values)
    })
    return unsubscribe
  }, [form])

  const { runOptimization, optimizedPositionSet, optimizationStatus } = useOptimization(
    instrument,
    modelId,
    endDate,
    base,
    formValues,
  )

  const isOptimizing = optimizationStatus === LOADING
  const hasError = optimizationStatus === ERROR
  const canOptimize = Boolean(
    modelMetadata?.id
    && instrument?.id
    && !isOptimizing
    && modelId
    && endDate,
  )

  const isSaveRef = useRef<boolean>(false)

  useEffect(() => {
    if (hasError) {
      isSaveRef.current = false
      setIsOptimizedExperimentsDrawerOpen(false)
    } else if (optimizedPositionSet) {
      setComparisonInstrument(optimizedPositionSet)
      if (isSaveRef.current) {
        setIsOptimizedExperimentsDrawerOpen(false)
        isSaveRef.current = false
      }
    }
  }, [optimizedPositionSet, setComparisonInstrument, setIsOptimizedExperimentsDrawerOpen, hasError])

  // Only run and save preview configuration when the panel is open (live preview/edit mode)
  // If there's a change, wait 3 seconds to see if there's another change
  // If there's no change, run the optimization
  const debouncedRunOptimization = useDebouncedCallback(() => {
    if (canOptimize && isOptimizedExperimentsDrawerOpen && form.state.canSubmit) {
      try {
        const values = form.state.values
        const currentConfiguration = transformFormToConfiguration(endDate, values.setup, values.rules)
        setFormError(null)
        runOptimization()
        setPreviewOptimizeConfiguration(currentConfiguration)
      } catch {
        setFormError("Invalid form input. Please check your inputs and try again.")
      }
    }
  }, 3000)

  const [isDirty, setIsDirty] = useState(form.state.isDirty)

  useEffect(() => {
    const unsubscribe = form.store.subscribe(() => {
      setIsDirty(form.state.isDirty)
    })
    return unsubscribe
  }, [form])

  useEffect(() => {
    if (isDirty) {
      debouncedRunOptimization()
    }
  }, [formValues, isDirty, debouncedRunOptimization])

  useEffect(() => {
    const hasDateChanged = initialEndDate.current !== endDate
    if (hasDateChanged && isOptimizedExperimentsDrawerOpen) {
      debouncedRunOptimization()
    }
  }, [endDate, isOptimizedExperimentsDrawerOpen, debouncedRunOptimization])

  const handleSaveAndOptimize = useCallback(() => {
    form.handleSubmit()
  }, [form])

  const handleClosePanel = useCallback(() => {
    setIsOptimizedExperimentsDrawerOpen(false)
  }, [setIsOptimizedExperimentsDrawerOpen])

  return (
    <div className={classNames("optimize-experiments-content", { "use-dashboard-layout": isDashboardOpLayoutEnabled })}>
      <div className="content">
        <div className="header">
          <TuneOutlined color="primary" />
          <Typography variant="h6">
            <span>Optimized Experiments</span>
          </Typography>
          <div className="header-actions">
            <IconButton onClick={handleClosePanel} size="medium" color="inherit">
              <Close />
            </IconButton>
          </div>
        </div>
        <OptimizeFormContext.Provider value={form}>
          <Box className="body">
            <Box className="body-section">
              <Setup currentTotalRiskValue={totalRiskValue} totalRiskStatus={totalRiskStatus} />
            </Box>
            <Box className="body-section">
              <Rules />
            </Box>
            <Box className="actions">
            {isOptimizing ? (
              <AnimatedLoadingButton text="Optimizing..." />
            ) : (
              <Button
                variant="contained"
                color={hasError ? "error" : "primary"}
                onClick={handleSaveAndOptimize}
                disabled={!canOptimize || !form.state.canSubmit}
              >
                {hasError ? "Optimization Failed - Retry" : "Run and Save"}
              </Button>
            )}
            </Box>
            <Error error={formError} />
          </Box>
        </OptimizeFormContext.Provider>
      </div>
    </div>
  )
}
